import type { IAhuBoardSection, IAhuBoardTunnel, ICmsAhuDoc } from 'models';
import { cmsTunnelIndexes } from '../forms/cmsDoc';

/**
 * Hybrid AHU Add-Section rows — a port of the `is_hybrid` branches of
 * `fn.tools.get_possible_bindings_for_insertion` (fn_tools.js:308-420 single
 * tunnel, :423-520 dual tunnel, on KA-x-user-skill).
 *
 * The shape legacy settled on, quoting its own comment: "The legacy
 * per-position / per-index / upstream-of / back-to-back rows are all removed
 * — every 'Add Section' click in Power mode makes a whole new tunnel with one
 * section in it, and users arrange them via x_offset in the Data Bar."
 *
 * So there is exactly ONE row per `row:level` — every existing one, plus (once
 * the AHU has at least one section somewhere) one new OU level per existing
 * row and one new SBS row. Every row carries X-Offset + Direction for the
 * tunnel it is about to create, and none of them carries a "downstream of"
 * section picker: in hybrid mode this dialog never grows an existing tunnel.
 *
 * The one exception is the bootstrap case — a still-sectionless AHU. When it
 * still has its starter tunnel, `row:level` 1:1 is by construction that
 * tunnel's address, so the row targets it directly (`existingTunnelId`)
 * instead of creating a second one beside it; its X-Offset/Direction are
 * PATCHed onto the starter tunnel rather than sent with a create. When it has
 * no tunnels at all — every section deleted off it, which takes the last
 * tunnel with it (AHUSection.delete) — the same 1:1 row is offered and
 * CREATES the tunnel on INSERT.
 */

export interface HybridRowTarget {
  row: number;
  level: number;
  /** Placeholder — the backend's auto-address signal renumbers peers on save. */
  position: number;
  index_id: number;
  /** `target_tunnel_id` — reuse this tunnel instead of creating one. */
  existingTunnelId?: number;
  /** `peer_tunnel_id` — an existing tunnel in the same row:level, if any. */
  peerTunnelId?: number;
  /** Airflow of the most recently added tunnel at this LEVEL, else LTR. */
  defaultAirflowDirectionId: number;
}

export interface HybridInsertionRow {
  targets: HybridRowTarget[];
  /** `{{ addressing }}` — `row:level`, two fields, as the template renders it. */
  label: string;
  /** `is_back_to_back` — drives the row's `back-to-back-row` class. */
  isBackToBack: boolean;
}

/**
 * The airflow legacy defaults a new tunnel at `level` to: that of the
 * most-recently-added tunnel on the same level (largest id), falling back to
 * Left-to-Right when the level is still empty.
 */
const latestAirflowAtLevel = (tunnels: IAhuBoardTunnel[], level: number): number => {
  let latest: IAhuBoardTunnel | undefined;
  tunnels.forEach((t) => {
    if ((t.level ?? 1) !== level) return;
    if (!latest || t.id > latest.id) latest = t;
  });
  return latest ? Number(latest.airflow_direction_id) || 1 : 1;
};

const sortRowLevels = (rowLevels: Set<string>): Array<[number, number]> =>
  Array.from(rowLevels)
    .map((rl) => rl.split(':').map(Number) as [number, number])
    .sort(([ar, al], [br, bl]) => (ar !== br ? ar - br : al - bl));

export function getPossibleHybridBindings(args: {
  isDualTunnel: boolean;
  tunnels: IAhuBoardTunnel[];
  sectionsByTunnel: Record<number, IAhuBoardSection[]>;
  cms: ICmsAhuDoc | undefined;
}): HybridInsertionRow[] {
  const { isDualTunnel, tunnels, sectionsByTunnel, cms } = args;

  /**
   * The CMS tunnel index a `row:level` cell maps to — the legacy index `value`
   * is "X_Y" with X = row and Y = level (tunnel_viewset `_row_level_from_index`
   * parses it back the same way). Only reached when no tunnel can lend its own
   * `index_id`, i.e. on an AHU with no tunnels at all; everywhere else the
   * peer's index is what legacy copies.
   */
  const cmsIndexIdFor = (r: number, l: number): number | undefined => {
    const row = cmsTunnelIndexes(cms).find((i) => String(i.value ?? '') === `${r}_${l}`);
    return row ? Number(row.id) : undefined;
  };

  const rowLevels = new Set<string>();
  let maxRow = 0;
  const maxLevelByRow = new Map<number, number>();
  tunnels.forEach((t) => {
    const r = t.row;
    const l = t.level;
    if (r == null || l == null) return;
    rowLevels.add(`${r}:${l}`);
    if (r > maxRow) maxRow = r;
    if (l > (maxLevelByRow.get(r) ?? 0)) maxLevelByRow.set(r, l);
  });

  // An AHU with NO tunnels is a live state since deleting a section deletes the
  // tunnel it empties, the unit's last one included (AHUSection.delete). The
  // rows below are all derived from existing tunnels, so such a unit produced
  // an EMPTY dialog — hybrid Add Section offering nothing to insert into, with
  // no way back into hybrid short of rebuilding the unit. Seed the cell a unit
  // starts at; every row built from it finds no peer and creates its tunnel.
  if (!rowLevels.size) {
    rowLevels.add('1:1');
    maxRow = 1;
    maxLevelByRow.set(1, 1);
  }

  // On a still-fully-empty AHU only the 1:1 cell is offered — don't tempt a
  // Power User into bootstrapping extra rows/levels before the very first
  // section has been placed.
  const hasAnySections = tunnels.some((t) => (sectionsByTunnel[t.id] ?? []).length > 0);

  // The peer also supplies the new tunnel's width and height (boardActions
  // sends them with the create), so it has to be the tunnel the user thinks
  // of as "the previous section" — the most recently added peer in the chain,
  // not whichever of them the list happens to hold first. Same largest-id rule
  // as latestAirflowAtLevel above, and as the backend's own inheritance in
  // AHUTunnels.save().
  const peerAt = (r: number, l: number) =>
    tunnels.reduce<IAhuBoardTunnel | undefined>(
      (acc, t) =>
        t.row === r && t.level === l && (!acc || t.id > acc.id) ? t : acc,
      undefined
    ) ??
    // Fall back to the tunnel this one would sit on (the level below, for a
    // new OU level) or beside (the row to its left, for a new SBS row), purely
    // to borrow a valid legacy `index_id` for the create.
    tunnels.find(
      (t) =>
        t.row === (l > 1 ? r : r - 1) &&
        t.level === (l > 1 ? l - 1 : l) &&
        (t.position ?? 1) === 1
    );

  if (!isDualTunnel) {
    if (hasAnySections) {
      maxLevelByRow.forEach((level, r) => rowLevels.add(`${r}:${level + 1}`));
      rowLevels.add(`${maxRow + 1}:1`);
    }

    return sortRowLevels(rowLevels).map(([r, l]) => {
      const peer = peerAt(r, l);
      // On a still-sectionless AHU `peer` IS the AHU's own starter tunnel —
      // target it directly, or bootstrapping the very first section duplicates
      // it (a new 1:1:2 alongside an untouched 1:1:1) instead of using it.
      const targetExisting = !hasAnySections && peer ? peer.id : undefined;
      return {
        targets: [
          {
            row: r,
            level: l,
            position: 1,
            index_id: Number(peer?.index_id ?? cmsIndexIdFor(r, l) ?? 0),
            existingTunnelId: targetExisting,
            peerTunnelId: targetExisting == null ? peer?.id : undefined,
            defaultAirflowDirectionId: latestAirflowAtLevel(tunnels, l),
          },
        ],
        label: `${r}:${l}`,
        isBackToBack: targetExisting == null,
      };
    });
  }

  // Dual-tunnel components (Heat Wheel / Plate HE) collapse to one row per
  // (bottom row:level, top row:level) pair — each click creates TWO tunnels
  // with a linked section pair, sharing one X-Offset and one direction each.
  const pairs = new Set<string>();
  rowLevels.forEach((rl) => {
    const [r, l] = rl.split(':').map(Number);
    if (l >= 2) pairs.add(`${r}:${l - 1}::${r}:${l}`);
  });
  maxLevelByRow.forEach((level, r) => {
    pairs.add(`${r}:${level}::${r}:${level + 1}`);
    // SBS dual pairs, skipped for rows without a level 1 to avoid nonsense.
    if (rowLevels.has(`${r}:1`)) pairs.add(`${r}:1::${r + 1}:1`);
  });

  return Array.from(pairs)
    .sort()
    .map((pair) => {
      const [bot, top] = pair.split('::');
      const [br, bl] = bot.split(':').map(Number);
      const [tr, tl] = top.split(':').map(Number);
      const botPeer = tunnels.find((t) => t.row === br && t.level === bl);
      const topPeer = tunnels.find((t) => t.row === tr && t.level === tl);
      // Same bootstrap case as the single-tunnel branch above: on a
      // still-sectionless AHU the bottom cell IS the starter tunnel's address,
      // so target it directly instead of creating a second tunnel beside it.
      // Without this the first Heat Wheel / Plate HE insert leaves the
      // untouched starter behind as an empty extra tunnel in the pair's bottom
      // row — which, holding the lowest id, is `tunnels[0]` for every consumer
      // ordered by id, so the board's `#canvas_section.empty` gate reads the
      // whole AHU as sectionless and shows the onboarding card instead of the
      // unit the user just built.
      const botExisting = !hasAnySections && botPeer ? botPeer.id : undefined;
      return {
        targets: [
          {
            row: br,
            level: bl,
            position: 1,
            index_id: Number(botPeer?.index_id ?? cmsIndexIdFor(br, bl) ?? 0),
            existingTunnelId: botExisting,
            peerTunnelId: botExisting == null ? botPeer?.id : undefined,
            defaultAirflowDirectionId: latestAirflowAtLevel(tunnels, bl),
          },
          {
            row: tr,
            level: tl,
            position: 1,
            index_id: Number(
              (topPeer ?? botPeer)?.index_id ?? cmsIndexIdFor(tr, tl) ?? 0
            ),
            // Same `?? botPeer` fallback as index_id above: a pair's top cell
            // is usually empty, and without a peer the create sends no
            // width/height at all — so the top tunnel came out at the backend
            // defaults while the bottom one kept the size of the chain it
            // joined, and `defaultRowYOffset` laid a new SBS row out from a
            // width of 0.
            peerTunnelId: (topPeer ?? botPeer)?.id,
            defaultAirflowDirectionId: latestAirflowAtLevel(tunnels, tl),
          },
        ],
        label: `${br}:${bl} + ${tr}:${tl}`,
        isBackToBack: true,
      };
    });
}
